import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Keyboard } from '@capacitor/keyboard';
import { Network } from '@capacitor/network';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';

/**
 * A deliberately small boundary between Athena's web UI and native Capacitor
 * plugins.  Keeping every native check here lets the PWA keep its existing
 * behaviour when it is opened in a normal browser.
 */
const getPlatform = () => {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
};

const getIsNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

export const nativePlatform = getPlatform();
export const isNative = getIsNative();
export const isAndroid = isNative && nativePlatform === 'android';
export const isWeb = !isNative;

export const NATIVE_BACK_EVENT = 'athena-native-back';
export const NATIVE_KEYBOARD_EVENT = 'athena-native-keyboard';

const ATHENA_DARK = '#020817';
const noop = () => {};

function canUseDom() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

async function removePluginListener(handle) {
  try {
    await handle?.remove?.();
  } catch {
    // A listener can already be gone when Android destroys a WebView.
  }
}

function updateKeyboardInset(keyboardHeight = 0) {
  if (!canUseDom()) return;

  const height = Number.isFinite(Number(keyboardHeight))
    ? Math.max(0, Math.round(Number(keyboardHeight)))
    : 0;

  document.documentElement.style.setProperty('--athena-native-keyboard-height', `${height}px`);
  document.documentElement.dataset.athenaKeyboard = height > 0 ? 'open' : 'closed';
  window.dispatchEvent(new CustomEvent(NATIVE_KEYBOARD_EVENT, { detail: { height } }));
}

/**
 * Applies only native shell details. It is intentionally a no-op in the PWA.
 */
export async function initializeNativeShell() {
  if (!isNative) return;

  if (canUseDom()) {
    document.documentElement.dataset.athenaPlatform = nativePlatform;
    updateKeyboardInset(0);
  }

  // "Dark" in the Capacitor StatusBar enum means light text over a dark bar.
  await Promise.allSettled([
    StatusBar.setStyle({ style: Style.Dark }),
    StatusBar.setOverlaysWebView({ overlay: false }),
    StatusBar.setBackgroundColor({ color: ATHENA_DARK }),
  ]);
}

let splashHasBeenHidden = false;

export async function hideNativeSplash() {
  if (!isNative || splashHasBeenHidden) return;
  splashHasBeenHidden = true;

  try {
    await SplashScreen.hide({ fadeOutDuration: 180 });
  } catch {
    // The plugin can auto-hide on a configuration that predates this shell.
  }
}

/**
 * Uses the system Custom Tabs / in-app browser on native. On web it preserves
 * the existing new-tab behaviour and never opens executable URL schemes.
 */
export async function openExternal(url, options = {}) {
  if (typeof url !== 'string' || !url.trim()) return false;

  let safeUrl;
  try {
    safeUrl = new URL(url, canUseDom() ? window.location.href : undefined);
  } catch {
    return false;
  }

  if (!['https:', 'http:', 'mailto:', 'tel:'].includes(safeUrl.protocol)) return false;

  if (isNative) {
    try {
      await Browser.open({
        url: safeUrl.href,
        toolbarColor: ATHENA_DARK,
      });
      return true;
    } catch {
      return false;
    }
  }

  if (!canUseDom()) return false;
  const opened = window.open(safeUrl.href, options.windowName || '_blank', 'noopener,noreferrer');
  if (opened) opened.opener = null;
  return Boolean(opened);
}

export function isSafeExternalUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  try {
    const parsed = new URL(url, canUseDom() ? window.location.href : undefined);
    return ['https:', 'http:', 'mailto:', 'tel:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

function normalizePathSegments(value) {
  return String(value || '')
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      try {
        return encodeURIComponent(decodeURIComponent(segment));
      } catch {
        return encodeURIComponent(segment);
      }
    })
    .join('/');
}

function isAthenaWebHost(hostname) {
  const host = String(hostname || '').toLowerCase();
  return host === 'athena-8ob.pages.dev' || host.endsWith('.athena-8ob.pages.dev');
}

// Fase 5 action links are deliberately a closed navigation vocabulary. The
// resolver does not preserve their query/hash values, so an external source
// cannot attach a command payload, a redirect URL, or conversation text.
const ATHENA_ACTION_LINKS = new Set([
  'open-home',
  'open-quick',
  'open-chat',
  'open-project',
  'open-calendar',
  'open-task',
  'open-workspace',
  'start-voice',
  'new-task',
  'new-reminder',
  'capture-idea',
  'import-content',
]);

/**
 * Prepares the route mapping for Android intent filters now, without assuming
 * that a public App Link domain has already been configured.
 *
 *   athena://action/new-task -> /?athena_action=new-task
 *
 * The custom scheme is deliberately narrower than browser navigation: it
 * mirrors AthenaIntentRouter.java exactly.  Dynamic IDs, query strings and
 * fragments are not a native command channel and must not bypass that
 * allowlist on their way into the WebView.
 */
export function resolveAthenaDeepLink(rawUrl) {
  if (typeof rawUrl !== 'string' || !rawUrl.trim()) return null;

  let parsed;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }

  const isCustomScheme = parsed.protocol === 'athena:';
  const isKnownWebLink = ['https:', 'http:'].includes(parsed.protocol) && isAthenaWebHost(parsed.hostname);
  if (!isCustomScheme && !isKnownWebLink) return null;

  if (isKnownWebLink) {
    const path = parsed.pathname || '/';
    if (path === '/' || path === '/login' || path === '/visual-call' || path.startsWith('/workspace')) {
      return `${path}${parsed.search || ''}${parsed.hash || ''}`;
    }
    return null;
  }

  if (parsed.hostname.toLowerCase() !== 'action' || parsed.search || parsed.hash) return null;
  const actionSegments = normalizePathSegments(parsed.pathname).split('/').filter(Boolean);
  if (actionSegments.length !== 1) return null;
  const action = actionSegments[0].toLowerCase();
  return ATHENA_ACTION_LINKS.has(action) ? `/?athena_action=${encodeURIComponent(action)}` : null;
}

export async function getNativeLaunchUrl() {
  if (!isNative) return null;
  try {
    const launch = await CapacitorApp.getLaunchUrl();
    return launch?.url || null;
  } catch {
    return null;
  }
}

export async function addNativeUrlListener(listener) {
  if (!isNative) return noop;
  try {
    const handle = await CapacitorApp.addListener('appUrlOpen', listener);
    return () => removePluginListener(handle);
  } catch {
    return noop;
  }
}

/**
 * Keeps native-only capabilities (voice, camera, future background-safe
 * features) aligned with Android lifecycle without making PWA callers branch.
 */
export async function addNativeAppStateListener(listener) {
  if (!isNative || typeof listener !== 'function') return noop;
  try {
    const handle = await CapacitorApp.addListener('appStateChange', listener);
    return () => removePluginListener(handle);
  } catch {
    return noop;
  }
}

export async function addNativeBackButtonListener(listener) {
  if (!isAndroid) return noop;
  try {
    const handle = await CapacitorApp.addListener('backButton', listener);
    return () => removePluginListener(handle);
  } catch {
    return noop;
  }
}

export async function minimizeNativeApp() {
  if (!isAndroid) return;
  try {
    await CapacitorApp.minimizeApp();
  } catch {
    // Never force-close the app if Android declines to minimize it.
  }
}

export async function addNativeKeyboardListener(listener) {
  if (!isNative) return noop;

  const onShow = (info) => {
    const height = info?.keyboardHeight || 0;
    updateKeyboardInset(height);
    listener?.({ height, visible: true });
  };
  const onHide = () => {
    updateKeyboardInset(0);
    listener?.({ height: 0, visible: false });
  };

  try {
    // `will*` deixa o layout acompanhar a animação inicial; `did*` confirma
    // a altura final em aparelhos que só publicam o inset ao concluir.
    const handles = await Promise.all([
      Keyboard.addListener('keyboardWillShow', onShow),
      Keyboard.addListener('keyboardDidShow', onShow),
      Keyboard.addListener('keyboardWillHide', onHide),
      Keyboard.addListener('keyboardDidHide', onHide),
    ]);
    return () => Promise.all(handles.map(removePluginListener));
  } catch {
    return noop;
  }
}

function getBrowserNetworkStatus() {
  if (typeof navigator === 'undefined') return { connected: true, connectionType: 'unknown' };
  return {
    connected: navigator.onLine !== false,
    connectionType: navigator.onLine === false ? 'none' : 'unknown',
  };
}

export async function getConnectionStatus() {
  if (!isNative) return getBrowserNetworkStatus();
  try {
    return await Network.getStatus();
  } catch {
    return getBrowserNetworkStatus();
  }
}

export async function addConnectionStatusListener(listener) {
  if (typeof listener !== 'function') return noop;

  if (isNative) {
    try {
      const handle = await Network.addListener('networkStatusChange', listener);
      return () => removePluginListener(handle);
    } catch {
      return noop;
    }
  }

  if (!canUseDom()) return noop;
  const onOnline = () => listener({ connected: true, connectionType: 'unknown' });
  const onOffline = () => listener({ connected: false, connectionType: 'none' });
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
}

/**
 * Lets native-only link interception keep regular same-origin React routes in
 * the WebView while sending source/document links to Android's browser UI.
 */
export function shouldOpenLinkExternally(anchor) {
  if (!anchor || !canUseDom()) return false;
  const href = anchor.getAttribute?.('href');
  if (!href || href.startsWith('#') || anchor.hasAttribute?.('download')) return false;

  let target;
  try {
    target = new URL(href, window.location.href);
  } catch {
    return false;
  }

  if (!['https:', 'http:', 'mailto:', 'tel:'].includes(target.protocol)) return false;
  if (target.protocol === 'mailto:' || target.protocol === 'tel:') return true;
  return target.origin !== window.location.origin;
}

/**
 * Documents Phase 1's stable API surface. Capability plugins can be added in
 * later phases without coupling Athena's conversation UI to native internals.
 */
export const nativeCapabilities = Object.freeze({
  platform: nativePlatform,
  native: isNative,
  notifications: isAndroid ? 'native-plugin-runtime-check' : 'web-fcm',
  // AthenaVoice queries the plugin itself before enabling a control. These
  // labels only describe the shell's possible integration route.
  microphone: isAndroid ? 'native-plugin-runtime-check' : 'web-media-permission',
  audio: isAndroid ? 'native-plugin-runtime-check' : 'web-audio',
  calendar: isAndroid ? 'native-plugin-runtime-check' : 'web-unavailable',
  alarms: isAndroid ? 'native-plugin-runtime-check' : 'planned',
  files: isAndroid ? 'native-plugin-runtime-check' : 'web-file-picker',
  sharing: isAndroid ? 'native-plugin-runtime-check' : 'web-share',
  camera: isAndroid ? 'native-plugin-runtime-check' : 'web-media-permission',
  backgroundTasks: 'planned',
});
